import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Card, Table, Form, Spinner } from 'react-bootstrap';
import { FiSearch, FiFilter, FiPlus, FiUsers, FiPhone, FiMapPin, FiChevronLeft, FiChevronRight } from 'react-icons/fi';

import { AppContext } from '../AppContext';
import { tre } from '../trebus';

export default function Customers() {
    const [customers, setCustomers] = useState({ data: [], current_page: 1, last_page: 1, total: 0 });
    const [searchString, setSearchString] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const context = useContext(AppContext);
    const navigate = useNavigate();

    // Load customers
    const loadCustomers = (page = 1, search = searchString) => {
        setIsLoading(true);
        tre.api('GET', '/companies/' + context.idCompany + '/customers', {
            context: context,
            params: {
                search: search,
                page: page,
                rows: 15
            },
            success: (r) => {
                setCustomers(r);
                setCurrentPage(page);
                setIsLoading(false);
            },
            error: () => setIsLoading(false)
        });
    };

    useEffect(() => {
        loadCustomers(1, searchString);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Debounced search
    useEffect(() => {
        const timer = setTimeout(() => {
            loadCustomers(1, searchString);
        }, 300);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchString]);

    const onSelectCustomer = (cust) => {
        navigate('/customer/' + cust.id);
    };

    const openToCreate = () => {
        navigate('/customer/create');
    };

    // Pagination
    const totalPages = customers.last_page || 1;
    const goToPage = (page) => {
        if (page >= 1 && page <= totalPages) {
            loadCustomers(page, searchString);
        }
    };

    // -----| Render |-----
    return (
        <div className="container py-4">
            {/* Header */}
            <div className="d-flex align-items-center justify-content-between mb-4">
                <div>
                    <h4 className="mb-1" style={{ color: '#202124', fontWeight: 500 }}>
                        <FiUsers className="me-2" style={{ color: '#1a73e8' }} />
                        Clientes
                    </h4>
                    <p className="text-muted mb-0" style={{ fontSize: '14px' }}>
                        {customers.total || 0} clientes en total
                    </p>
                </div>
                <Button variant="primary" onClick={openToCreate}>
                    <FiPlus className="me-2" />
                    Nuevo Cliente
                </Button>
            </div>

            {/* Filters - Inline style */}
            <div className="d-flex flex-wrap align-items-center gap-3 mb-4 p-3" style={{ backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                {/* Search */}
                <div className="position-relative" style={{ minWidth: '280px', flex: 1, maxWidth: '400px' }}>
                    <FiSearch
                        className="position-absolute"
                        style={{ left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#5f6368' }}
                    />
                    <Form.Control
                        type="text"
                        placeholder="Buscar por nombre, NIF o ciudad..."
                        value={searchString}
                        onChange={(e) => setSearchString(e.target.value)}
                        style={{ paddingLeft: '38px', backgroundColor: 'white' }}
                    />
                </div>

                {/* Clear filters */}
                {searchString && (
                    <Button
                        variant="link"
                        size="sm"
                        onClick={() => setSearchString('')}
                        style={{ color: '#ea4335', textDecoration: 'none' }}
                    >
                        <FiFilter className="me-1" /> Limpiar
                    </Button>
                )}
            </div>

            {/* Table Card */}
            <Card>
                {/* Top Pagination Info */}
                {!isLoading && customers.data && customers.data.length > 0 && (
                    <Card.Header className="d-flex flex-wrap justify-content-between align-items-center py-3 gap-3" style={{ backgroundColor: '#f8f9fa' }}>
                        <div className="d-flex align-items-center gap-4">
                            <span style={{ fontSize: '14px', color: '#5f6368' }}>
                                <strong style={{ color: '#202124' }}>{customers.total || 0}</strong> registros
                            </span>
                            <span style={{ fontSize: '14px', color: '#5f6368' }}>
                                Mostrando <strong style={{ color: '#202124' }}>{customers.from || 0}</strong> - <strong style={{ color: '#202124' }}>{customers.to || 0}</strong>
                            </span>
                            <span style={{ fontSize: '14px', color: '#5f6368' }}>
                                Página <strong style={{ color: '#202124' }}>{currentPage}</strong> de <strong style={{ color: '#202124' }}>{totalPages}</strong>
                            </span>
                        </div>
                        {totalPages > 1 && (
                            <div className="d-flex gap-2">
                                <Button variant="outline-secondary" size="sm" disabled={currentPage <= 1} onClick={() => goToPage(1)}>
                                    Primera
                                </Button>
                                <Button variant="outline-secondary" size="sm" disabled={currentPage <= 1} onClick={() => goToPage(currentPage - 1)}>
                                    <FiChevronLeft className="me-1" /> Anterior
                                </Button>
                                <Button variant="outline-secondary" size="sm" disabled={currentPage >= totalPages} onClick={() => goToPage(currentPage + 1)}>
                                    Siguiente <FiChevronRight className="ms-1" />
                                </Button>
                                <Button variant="outline-secondary" size="sm" disabled={currentPage >= totalPages} onClick={() => goToPage(totalPages)}>
                                    Última
                                </Button>
                            </div>
                        )}
                    </Card.Header>
                )}

                <Card.Body className="p-0">
                    {isLoading ? (
                        <div className="text-center py-5">
                            <Spinner animation="border" style={{ color: '#1a73e8' }} />
                            <p className="mt-3 text-muted">Cargando clientes...</p>
                        </div>
                    ) : customers.data && customers.data.length > 0 ? (
                        <Table hover responsive className="mb-0">
                            <thead style={{ backgroundColor: '#f8f9fa' }}>
                                <tr>
                                    <th className="py-3 px-3" style={{ fontWeight: 500, color: '#5f6368', fontSize: '13px' }}>CLIENTE</th>
                                    <th className="py-3" style={{ fontWeight: 500, color: '#5f6368', fontSize: '13px' }}>NIF</th>
                                    <th className="py-3" style={{ fontWeight: 500, color: '#5f6368', fontSize: '13px' }}>TELÉFONO</th>
                                    <th className="py-3 px-3" style={{ fontWeight: 500, color: '#5f6368', fontSize: '13px' }}>CIUDAD</th>
                                </tr>
                            </thead>
                            <tbody>
                                {customers.data.map((cust) => (
                                    <tr
                                        key={cust.id}
                                        onClick={() => onSelectCustomer(cust)}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        <td className="py-3 px-3">
                                            <div className="d-flex align-items-center">
                                                <div
                                                    className="d-flex align-items-center justify-content-center rounded-circle me-2"
                                                    style={{
                                                        width: '36px',
                                                        height: '36px',
                                                        backgroundColor: '#e8f0fe',
                                                        color: '#1a73e8',
                                                        fontSize: '14px',
                                                        fontWeight: 600
                                                    }}
                                                >
                                                    {(cust.name || '?').charAt(0).toUpperCase()}
                                                </div>
                                                <span style={{ color: '#202124', fontWeight: 500 }}>
                                                    {cust.name || '-'}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="py-3">
                                            <code style={{
                                                backgroundColor: '#f1f3f4',
                                                padding: '4px 8px',
                                                borderRadius: '4px',
                                                fontSize: '13px'
                                            }}>
                                                {cust.nif || '-'}
                                            </code>
                                        </td>
                                        <td className="py-3">
                                            {cust.phone ? (
                                                <span className="d-flex align-items-center text-muted" style={{ fontSize: '14px' }}>
                                                    <FiPhone className="me-1" size={14} />
                                                    {cust.phone}
                                                </span>
                                            ) : <span className="text-muted">-</span>}
                                        </td>
                                        <td className="py-3 px-3">
                                            {cust.city ? (
                                                <span className="d-flex align-items-center text-muted" style={{ fontSize: '14px' }}>
                                                    <FiMapPin className="me-1" size={14} />
                                                    {cust.city}
                                                </span>
                                            ) : <span className="text-muted">-</span>}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    ) : (
                        <div className="text-center py-5">
                            <FiUsers size={48} className="mb-3" style={{ color: '#dadce0' }} />
                            <p className="text-muted">No se encontraron clientes</p>
                        </div>
                    )}
                </Card.Body>

                {/* Bottom Pagination */}
                {customers.data && customers.data.length > 0 && totalPages > 1 && (
                    <Card.Footer className="d-flex flex-wrap justify-content-between align-items-center py-3 gap-3" style={{ backgroundColor: '#f8f9fa' }}>
                        <span style={{ fontSize: '14px', color: '#5f6368' }}>
                            Página <strong style={{ color: '#202124' }}>{currentPage}</strong> de <strong style={{ color: '#202124' }}>{totalPages}</strong>
                        </span>
                        <div className="d-flex gap-2">
                            <Button variant="outline-secondary" size="sm" disabled={currentPage <= 1} onClick={() => goToPage(1)}>
                                Primera
                            </Button>
                            <Button variant="outline-secondary" size="sm" disabled={currentPage <= 1} onClick={() => goToPage(currentPage - 1)}>
                                <FiChevronLeft className="me-1" /> Anterior
                            </Button>
                            <Button variant="outline-secondary" size="sm" disabled={currentPage >= totalPages} onClick={() => goToPage(currentPage + 1)}>
                                Siguiente <FiChevronRight className="ms-1" />
                            </Button>
                            <Button variant="outline-secondary" size="sm" disabled={currentPage >= totalPages} onClick={() => goToPage(totalPages)}>
                                Última
                            </Button>
                        </div>
                    </Card.Footer>
                )}
            </Card>

        </div>
    );
}
